'use strict';

/**
 * Comms encryption — per-thread random content key, wrapped per participant
 * via the platform's hybrid X25519 + ML-KEM-768 KEX, message bodies encrypted
 * with AES-256-GCM. Replaces the placeholder deriveKey from earlier
 * implementations (see GOTCHA-070).
 *
 * Wave A v1 (this module): server-side custodian model. The server can unwrap
 * the content key for any participant whose private key is currently
 * accessible (e.g., the logged-in session's identity-vault unlock). Disk-at-
 * rest is fully encrypted; no plaintext bodies ever persist.
 *
 * Wave A v2 (future): move wrap/unwrap to the client so the server never
 * holds raw private keys. The data structures here forward-compose with that
 * — wrap blobs are opaque to the server in either model; only WHERE the
 * unwrap happens changes.
 */

const crypto = require('crypto');
const hybridKex = require('../../crypto/hybrid-kex');

// Marker used in persisted thread records so loaders can refuse anything
// that doesn't carry a real encryption envelope.
const ENCRYPTION_VERSION = 'wave-a-v1-server-wrapped';

function generateContentKey() {
  return crypto.randomBytes(32);
}

function aesGcmEncrypt(plaintext, key) {
  const iv = crypto.randomBytes(12);
  const cipher = crypto.createCipheriv('aes-256-gcm', key, iv);
  const ciphertext = Buffer.concat([
    cipher.update(Buffer.from(plaintext, 'utf8')),
    cipher.final(),
  ]);
  const tag = cipher.getAuthTag();
  return {
    iv: iv.toString('base64'),
    ciphertext: ciphertext.toString('base64'),
    tag: tag.toString('base64'),
  };
}

function aesGcmDecrypt(envelope, key) {
  const iv = Buffer.from(envelope.iv, 'base64');
  const ciphertext = Buffer.from(envelope.ciphertext, 'base64');
  const tag = Buffer.from(envelope.tag, 'base64');
  const decipher = crypto.createDecipheriv('aes-256-gcm', key, iv);
  decipher.setAuthTag(tag);
  const plaintext = Buffer.concat([
    decipher.update(ciphertext),
    decipher.final(),
  ]);
  return plaintext.toString('utf8');
}

function toUint8(buf) {
  if (buf instanceof Uint8Array) return buf;
  if (Buffer.isBuffer(buf)) return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  if (typeof buf === 'string') return new Uint8Array(Buffer.from(buf, 'base64'));
  throw new TypeError('expected Uint8Array, Buffer, or base64 string');
}

function publicKeyFromStored(stored) {
  return {
    x25519: toUint8(stored.x25519),
    mlKem: toUint8(stored.mlKem),
  };
}

function privateKeyFromStored(stored) {
  return {
    x25519: toUint8(stored.x25519),
    mlKem: toUint8(stored.mlKem),
  };
}

function encodeBytes(u8) {
  return Buffer.from(u8.buffer, u8.byteOffset, u8.byteLength).toString('base64');
}

/**
 * Wrap a content key to a participant's hybrid public key.
 * Returns an opaque blob that only the participant can unwrap.
 */
function wrapContentKeyForParticipant(contentKey, participantPublicKey) {
  const recipientPub = publicKeyFromStored(participantPublicKey);
  const { kemCiphertext, x25519EphemeralPublic, sharedSecret, kexVersion } =
    hybridKex.encapsulate(recipientPub);

  const wrapEnvelope = aesGcmEncrypt(contentKey.toString('base64'), Buffer.from(sharedSecret));

  return {
    kemCiphertext: encodeBytes(kemCiphertext),
    x25519EphemeralPublic: encodeBytes(x25519EphemeralPublic),
    wrapEnvelope,
    kexVersion, // T513 — combiner version; key wraps persist in threads (at-rest)
  };
}

/**
 * Unwrap a content key using the participant's hybrid private key.
 */
function unwrapContentKeyForParticipant(wrap, participantPrivateKey) {
  const priv = privateKeyFromStored(participantPrivateKey);
  const kemCt = toUint8(wrap.kemCiphertext);
  const ephPub = toUint8(wrap.x25519EphemeralPublic);
  // T513 — key wraps are STORED (thread keyWraps); a pre-T513 wrap carries no
  // kexVersion and was sealed under the retired v1 combiner: ABSENT means v1.
  // An unknown value is refused by decapsulate; a wrong one fails at AES-GCM.
  const kexVersion = wrap.kexVersion === undefined ? hybridKex.KEX_V1 : wrap.kexVersion;
  const sharedSecret = hybridKex.decapsulate(priv, kemCt, ephPub, kexVersion);
  const contentKeyB64 = aesGcmDecrypt(wrap.wrapEnvelope, Buffer.from(sharedSecret));
  return Buffer.from(contentKeyB64, 'base64');
}

/**
 * Encrypt a message body with a content key.
 */
function encryptMessageBody(plaintextBody, contentKey) {
  return aesGcmEncrypt(plaintextBody, contentKey);
}

/**
 * Decrypt a message body envelope with a content key.
 */
function decryptMessageBody(envelope, contentKey) {
  return aesGcmDecrypt(envelope, contentKey);
}

module.exports = {
  ENCRYPTION_VERSION,
  generateContentKey,
  wrapContentKeyForParticipant,
  unwrapContentKeyForParticipant,
  encryptMessageBody,
  decryptMessageBody,
};
